/**
 * Typed client for /admin-feedback/* — backs the Feedback screen.
 *
 * FeedbackReport = an end-user complaint submitted from the desktop
 * Rewards Hub via POST /au/feedback. Stage 2 of the closed-loop
 * pipeline auto-triages with Claude (sets category + sometimes
 * auto-reply); Stage 3 (this screen) is where humans work whatever
 * the AI couldn't or shouldn't resolve.
 *
 * List endpoint uses mongoose-paginate-v2, same envelope as
 * escalations: `{ docs, totalDocs, limit, page, totalPages, ... }`.
 */
import { client } from './client';

export type FeedbackStatus =
  | 'open'
  | 'auto_replied'
  | 'in_progress'
  | 'awaiting_user'
  | 'resolved'
  | 'spam';

export type FeedbackCategory =
  | 'battery'
  | 'performance'
  | 'av'
  | 'payouts'
  | 'redemption'
  | 'login'
  | 'ui'
  | 'other'
  | 'unknown';

export type FeedbackPlatform = 'windows' | 'mac' | 'linux' | null;

export type ReplySource = 'ai' | 'ops' | null;

export interface PublisherRef {
  _id: string;
  name?: string;
  email?: string;
  companyName?: string;
}

export interface AppRef {
  _id: string;
  name?: string;
  appKey?: string;
  currencyName?: string;
}

export interface AppUserRef {
  _id: string;
  userKey?: string;
  publisherUserId?: string;
  installedAt?: string;
}

export interface FeedbackReport {
  _id: string;
  userCode?: string | null;
  message: string;
  status: FeedbackStatus;
  category: FeedbackCategory;
  replyText?: string;
  replySource?: ReplySource;
  repliedAt?: string | null;
  replyEmail?: string | null;
  platform?: FeedbackPlatform;
  appVersion?: string | null;
  publisherId?: PublisherRef | string | null;
  appId?: AppRef | string | null;
  appUserId?: AppUserRef | string | null;
  diagnostics?: Record<string, unknown>;
  internalNotes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FeedbackListResponse {
  docs: FeedbackReport[];
  totalDocs: number;
  limit: number;
  page: number;
  totalPages: number;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
}

export interface FeedbackStatsResponse {
  byStatus: Partial<Record<FeedbackStatus, number>>;
  byCategory: Partial<Record<FeedbackCategory, number>>;
  last24h: number;
}

export interface FeedbackListParams {
  page?: number;
  limit?: number;
  status?: FeedbackStatus | '';
  category?: FeedbackCategory | '';
  platform?: FeedbackPlatform | '';
  sort?: string;
}

export interface FeedbackUpdateInput {
  id: string;
  status?: FeedbackStatus;
  category?: FeedbackCategory;
  replyText?: string;
  internalNotes?: string;
}

// ─── Fetchers ─────────────────────────────────────────────────────────────

export async function fetchFeedback(
  params: FeedbackListParams
): Promise<FeedbackListResponse> {
  const cleanParams: Record<string, unknown> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== '' && v !== undefined && v !== null) {
      cleanParams[k] = v;
    }
  });
  const r = await client.get<FeedbackListResponse>('/admin-feedback', {
    params: cleanParams
  });
  return r.data;
}

export async function fetchFeedbackStats(): Promise<FeedbackStatsResponse> {
  const r = await client.get<FeedbackStatsResponse>('/admin-feedback/stats');
  return r.data;
}

export async function fetchFeedbackOne(id: string): Promise<FeedbackReport> {
  const r = await client.get<FeedbackReport>(`/admin-feedback/${id}`);
  return r.data;
}

export async function updateFeedback(
  input: FeedbackUpdateInput
): Promise<FeedbackReport> {
  const { id, ...body } = input;
  const r = await client.patch<FeedbackReport>(`/admin-feedback/${id}`, body);
  return r.data;
}
